import React, { useRef, useState, useEffect } from "react";
import URI from "urijs";
import OktaSignIn from "@okta/okta-signin-widget";
import AppConfig from "util/AppConfig";
import { Spinner } from "components/common";
import { FormGroup } from "components/bootstrap";
import OktaContainerStyles from "./OktaContainerStyles";
import CustomChosenStyles from "./external-assets/CustomChosenStyles";
import { LoginMetricsActions } from "./LoginMetricsStore";
import { createFailureLoginMetric, GRAYLOG_ERROR_CODES } from "./LoginMetrics";
const getCallbackUri = () => {
  const redirectUri = new URI(new URI().origin());
  redirectUri.path(AppConfig.gl2AppPathPrefix());
  redirectUri.segment("authorization-code/callback");
  return redirectUri.href();
};
const getExternalIdPConfig = (externalIdPId) => {
  if (!externalIdPId) {
    return null;
  }
  return {
    idpDisplay: "PRIMARY",
    idps: [{ id: `${externalIdPId}`, text: "Log in via external SAML IdP" }]
  };
};
const defaultRedirect = (url) => window.location.assign(url);
const OKTA_SCOPES = ["openid", "profile", "email"];
const OKTA_SCOPE = OKTA_SCOPES.join(" ");
const OidcLoginForm = ({
  configuration,
  nonce,
  checkSession = false,
  redirect = defaultRedirect
}) => {
  const widget = useRef();
  const [sessionState, setSessionState] = useState(checkSession ? "INITIAL" : "DONE");
  const widgetReady = useRef(false);
  const { base_url: baseUrl, oauth_client_id: oauthClientId, okta_external_idp_id: externalIdpId } = configuration;
  useEffect(() => {
    let shouldCleanUp = false;
    const cleanUp = () => {
      widget.current.off();
      widget.current.remove();
      widgetReady.current = false;
    };
    const onError = (err) => console.error(err);
    widget.current = new OktaSignIn({
      baseUrl,
      clientId: oauthClientId,
      redirectUri: getCallbackUri(),
      useClassicEngine: true,
      authParams: {
        issuer: baseUrl,
        authorizeUrl: `${baseUrl}/v1/authorize`,
        scopes: OKTA_SCOPES,
        state: nonce.state,
        responseType: "code",
        pkce: false,
        // we don't use nonce on the backend so set to `null`
        nonce: null
      },
      language: "en",
      i18n: {
        en: {
          "primaryauth.username.placeholder": "Email",
          "primaryauth.username.tooltip": "Email",
          "password.forgot.email.or.username.placeholder": "Email",
          "password.forgot.email.or.username.tooltip": "Email",
          "error.username.required": "Please enter an email address",
          "errors.E0000004": "Invalid email or password",
          "mfa.factors.dropdown.sr.text": "Using {0}, verify another way?",
          "mfa.factors.dropdown.title": "Select a verification method",
          "email.code.not.received": "Haven't received an email? "
        }
      },
      features: {
        hideSignOutLinkInMFA: true,
        webauthn: true,
        multiOptionalFactorEnroll: false
      },
      ...getExternalIdPConfig(externalIdpId)
    });
    widget.current.on("ready", () => {
      if (shouldCleanUp) {
        cleanUp();
        return;
      }
      widgetReady.current = true;
    });
    widget.current.on("afterError", (context, error) => {
      var _a, _b;
      if (context.controller !== "primary-auth") {
        return;
      }
      const oktaErrorCode = (_b = (_a = error == null ? void 0 : error.xhr) == null ? void 0 : _a.responseJSON) == null ? void 0 : _b.errorCode;
      if (oktaErrorCode) {
        LoginMetricsActions.post(createFailureLoginMetric("login", "okta", oktaErrorCode));
        return;
      }
      const statusCode = error == null ? void 0 : error.statusCode;
      if (statusCode) {
        LoginMetricsActions.post(createFailureLoginMetric("login", "http", statusCode));
        return;
      }
      LoginMetricsActions.post(createFailureLoginMetric("login", "gl", GRAYLOG_ERROR_CODES.oktaUnknownError));
    });
    widget.current.showSignInAndRedirect({ el: "#okta-root" }).catch(onError);
    if (checkSession) {
      setSessionState("CHECKING");
      widget.current.authClient.session.exists().then((exists) => {
        if (exists) {
          const loginUri = new URI(baseUrl);
          loginUri.segment("v1/authorize");
          loginUri.query({
            client_id: oauthClientId,
            response_type: "code",
            scope: OKTA_SCOPE,
            redirect_uri: getCallbackUri(),
            state: nonce.state
          });
          redirect(loginUri.href());
        } else {
          setSessionState("DONE");
        }
      });
    }
    return () => {
      if (widget.current && widgetReady.current) {
        cleanUp();
        return;
      }
      shouldCleanUp = true;
    };
  }, [checkSession, oauthClientId, baseUrl, nonce.state, externalIdpId, redirect]);
  const isBusy = sessionState !== "DONE";
  return /* @__PURE__ */ React.createElement(FormGroup, null, isBusy && /* @__PURE__ */ React.createElement(Spinner, null), /* @__PURE__ */ React.createElement(CustomChosenStyles, null), /* @__PURE__ */ React.createElement(OktaContainerStyles, { key: "okta-root", id: "okta-root", style: { display: isBusy ? "none" : "block" } }));
};
export default OidcLoginForm;
